import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { User } from '../models/User';
import { SettingsIcon, LogoutIcon } from './shared/Icons';
import './AccountMenu.css';

interface AccountMenuProps {
  user: User | null;
  onLogout: () => void;
}

const getInitials = (user: User | null): string => {
  if (!user) {
    return 'A';
  }

  const firstInitial = user.firstName?.trim().charAt(0) || '';
  const lastInitial = user.lastName?.trim().charAt(0) || '';
  const initials = `${firstInitial}${lastInitial}`.toUpperCase();

  if (initials) {
    return initials;
  }

  return user.email.charAt(0).toUpperCase();
};

const AccountMenu: React.FC<AccountMenuProps> = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || 'Account';
  const closeMenu = () => setIsOpen(false);

  const handleLogout = () => {
    closeMenu();
    onLogout();
  };

  return (
    <div className={`account-menu ${isOpen ? 'is-open' : ''}`} ref={menuRef}>
      <button
        type="button"
        className="account-menu-button"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Open account menu"
      >
        <span className="account-menu-avatar">{getInitials(user)}</span>
        <span className="account-menu-label">
          <span className="account-menu-email">{user?.email}</span>
        </span>
      </button>

      <div className="account-menu-panel" role="menu">
        <div className="account-menu-panel-header">
          <div className="account-menu-name">{displayName}</div>
        </div>
        <div className="account-menu-sheet-handle" aria-hidden="true" />

        <div className="account-menu-actions">
          <Link
            to="/dashboard/settings"
            className="account-menu-link"
            role="menuitem"
            onClick={closeMenu}
          >
            <SettingsIcon className="me-2" /> Settings
          </Link>
          <button
            type="button"
            className="account-menu-link account-menu-logout"
            role="menuitem"
            onClick={handleLogout}
          >
            <LogoutIcon className="me-2" /> Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountMenu;